/*
 * Cart-drawer upsell — drag-to-scroll + Shopify dynamic recommendations.
 *
 * Two responsibilities:
 *  1. Mouse drag-to-scroll and prev/next arrows for .cd-upsell__track
 *     (delegated on `document` — survives cart section re-renders).
 *  2. After each cart render, replace the Liquid product cards with live
 *     results from Shopify's Product Recommendations API (intent=complementary).
 *     Falls back silently to the Liquid-rendered list when the API returns 0.
 *     Also eliminates product-tag badge leakage since JS cards never call
 *     product-tags-badges.liquid.
 */
(function () {
  var SELECTOR = '.cd-upsell__track';
  var drag = null;

  /* ── Drag-to-scroll ───────────────────────────────────────────────────────── */

  document.addEventListener('pointerdown', function (e) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    var track = e.target.closest && e.target.closest(SELECTOR);
    if (!track) return;
    // Never hijack a press that starts on an interactive control (the quick-add "+" button or a
    // product link) as a drag-to-scroll: a few px of pointer travel would mark it `moved`, and
    // endDrag() then swallows the release click (preventDefault + stopPropagation) — so the add
    // (or link) never fires. Desktop-only symptom, since touch skips this whole handler.
    if (e.target.closest('button, a')) return;
    drag = {
      track: track,
      startX: e.clientX,
      startLeft: track.scrollLeft,
      moved: false,
    };
  });

  document.addEventListener(
    'pointermove',
    function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.startX;
      if (!drag.moved && Math.abs(dx) < 5) return;
      if (!drag.moved) {
        drag.moved = true;
        drag.track.classList.add('is-dragging');
      }
      drag.track.scrollLeft = drag.startLeft - dx;
      e.preventDefault();
    },
    { passive: false }
  );

  function endDrag() {
    if (!drag) return;
    var track = drag.track;
    if (drag.moved) {
      var swallow = function (ev) {
        ev.stopPropagation();
        ev.preventDefault();
      };
      track.addEventListener('click', swallow, true);
      setTimeout(function () {
        track.removeEventListener('click', swallow, true);
      }, 0);
    }
    track.classList.remove('is-dragging');
    drag = null;
  }

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  /* ── Prev / next arrows ───────────────────────────────────────────────────── */

  function trackOf(root) {
    return root ? root.querySelector(SELECTOR) : null;
  }

  document.addEventListener('click', function (e) {
    var btn =
      e.target.closest && e.target.closest('[data-cd-upsell-prev],[data-cd-upsell-next]');
    if (!btn) return;
    var track = trackOf(btn.closest('.cd-upsell'));
    if (!track) return;
    var slide = track.querySelector('.cd-upsell__slide');
    var gap = parseFloat(getComputedStyle(track).columnGap) || 10;
    var step = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth * 0.9;
    var dir = btn.hasAttribute('data-cd-upsell-next') ? 1 : -1;
    track.scrollBy({ left: dir * step, behavior: 'smooth' });
  });

  function updateArrows(track) {
    var root = track.closest('.cd-upsell');
    var controls = root && root.querySelector('.cd-upsell__controls');
    if (!controls) return;
    var max = track.scrollWidth - track.clientWidth;
    var scrollable = max > 1;
    controls.hidden = !scrollable;
    var prev = controls.querySelector('[data-cd-upsell-prev]');
    var next = controls.querySelector('[data-cd-upsell-next]');
    if (prev) prev.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= max - 1;
    var fill = controls.querySelector('.cd-upsell__progress-fill');
    if (fill) {
      var pct = scrollable ? (track.scrollLeft / max) * 100 : 0;
      fill.style.width = Math.max(0, Math.min(100, pct)) + '%';
    }
  }

  document.addEventListener(
    'scroll',
    function (e) {
      var t = e.target;
      if (t && t.classList && t.classList.contains('cd-upsell__track')) updateArrows(t);
    },
    true
  );

  function setupTrack(track) {
    if (track.__cdArrowsInit) return;
    track.__cdArrowsInit = true;
    updateArrows(track);
    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        updateArrows(track);
      }).observe(track);
    }
  }

  function scanTracks() {
    document.querySelectorAll(SELECTOR).forEach(setupTrack);
  }

  /* ── Shopify Product Recommendations ──────────────────────────────────────
   * Fetches /recommendations/products.json?intent=complementary for the first
   * product in cart, then builds and injects slide HTML into .cd-upsell__track.
   *
   * Loop guard: updating track.innerHTML triggers the MutationObserver again.
   * _recUpdating is set true before the write and reset on the next tick so the
   * observer second fire is a no-op.
   * ──────────────────────────────────────────────────────────────────────── */
  var _recUpdating = false;
  var _lastCartSig = null;

  var STAR_PATH = 'M4.5 6.537 2.183 7.953l.63-2.642L.75 3.544l2.707-.216L4.5.82l1.043 2.508 2.707.216-2.062 1.767.63 2.642z';

  function starSVG(filled) {
    return '<svg width="16" height="16" viewBox="0 0 9 9" xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true">' +
      '<path fill="' + (filled ? 'currentColor' : 'rgb(227,227,227)') + '" d="' + STAR_PATH + '"/></svg>';
  }

  function halfStarSVG(uid) {
    var id = 'cdh-' + uid;
    return '<svg width="16" height="16" viewBox="0 0 9 9" xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true">' +
      '<defs><clipPath id="' + id + '"><rect x="0" y="0" width="4.5" height="9"/></clipPath></defs>' +
      '<path fill="rgb(227,227,227)" d="' + STAR_PATH + '"/>' +
      '<path fill="currentColor" clip-path="url(#' + id + ')" d="' + STAR_PATH + '"/>' +
      '</svg>';
  }

  // Deterministic pseudo-random from product ID so same product always gets same rating.
  // Uses a simple LCG. Bitwise ops force 32-bit integer arithmetic in JS.
  function seededRand(seed, salt, min, max) {
    var h = ((seed ^ salt) * 1664525 + 1013904223) & 0x7fffffff;
    return min + (h % (max - min + 1));
  }

  function buildStars(productId) {
    // Rating 4.0–5.0 in 0.1 steps (11 values: index 0–10 → /10 + 4).
    var idx = seededRand(productId, 0, 0, 10);
    var rating = 4 + idx / 10;
    var count  = seededRand(productId, 777, 1, 50);

    var filled = Math.floor(rating);
    var dec    = Math.round((rating - filled) * 10); // tenths: 0–9
    var half   = dec >= 3 && dec < 7;
    if (dec >= 7) filled++;

    var html = '';
    for (var i = 1; i <= 5; i++) {
      if (i <= filled) {
        html += '<span class="cd-upsell__star">' + starSVG(true) + '</span>';
      } else if (half && i === filled + 1) {
        html += '<span class="cd-upsell__star cd-upsell__star--half">' + halfStarSVG(productId + '-' + i) + '</span>';
        half = false;
      } else {
        html += '<span class="cd-upsell__star cd-upsell__star--empty">' + starSVG(false) + '</span>';
      }
    }

    return {
      starsHTML: '<span class="cd-upsell__stars" role="img" aria-label="' + rating.toFixed(1) + ' de 5">' + html + '</span>' +
                 '<span class="cd-upsell__card-count">(' + count + ')</span>',
    };
  }

  function moneyFmt(cents) {
    if (window.Shopify && Shopify.formatMoney) {
      var fmt = (window.theme && window.theme.moneyFormat) || null;
      return Shopify.formatMoney(cents, fmt);
    }
    return '$ ' + Math.round(cents / 100).toLocaleString('es-CO');
  }

  function parseMoney(value) {
    var amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
  }

  function esc(str) {
    return (str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  function buildVariantButtons(product, selectedVariantId) {
    if (!product || !product.variants || product.variants.length <= 1) return '';

    return '<div class="cd-upsell__variants" role="list" aria-label="Variantes de ' + esc(product.title) + '">' +
      product.variants.map(function (variant) {
        var isActive = String(variant.id) === String(selectedVariantId);
        var variantImage = (variant.featured_image && variant.featured_image.src) || product.featured_image || '';
        var compareAt = variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : '';
        var url = esc(product.url) + '?variant=' + variant.id;
        return '<button type="button" class="cd-upsell__variant-btn' + (isActive ? ' is-active' : '') + '"' +
          ' data-cd-variant-id="' + variant.id + '"' +
          ' data-cd-price="' + variant.price + '"' +
          ' data-cd-compare-price="' + compareAt + '"' +
          ' data-cd-title="' + esc(product.title) + '"' +
          ' data-cd-url="' + url + '"' +
          ' data-cd-image="' + esc(variantImage) + '"' +
          ' data-cd-image-alt="' + esc(product.title) + '"' +
          ' aria-pressed="' + (isActive ? 'true' : 'false') + '"' +
          (variant.available ? '' : ' disabled') +
          '>' + esc(variant.title) + '</button>';
      }).join('') +
      '</div>';
  }

  function applySelectedVariant(card, button) {
    if (!card || !button) return;

    card.querySelectorAll('[data-cd-variant-id]').forEach(function (item) {
      var isActive = item === button;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    var variantId = button.getAttribute('data-cd-variant-id') || '';
    var price = parseMoney(button.getAttribute('data-cd-price'));
    var compareAt = parseMoney(button.getAttribute('data-cd-compare-price'));
    var title = button.getAttribute('data-cd-title') || '';
    var url = button.getAttribute('data-cd-url') || '';
    var image = button.getAttribute('data-cd-image') || '';
    var imageAlt = button.getAttribute('data-cd-image-alt') || title;

    var priceEl = card.querySelector('[data-cd-upsell-price]');
    var compareEl = card.querySelector('[data-cd-upsell-compare-price]');
    var titleEl = card.querySelector('[data-cd-upsell-title]');
    var imageEl = card.querySelector('.cd-upsell__card-image');
    var imageLink = card.querySelector('.cd-upsell__card-image-link');
    var hiddenInput = card.querySelector('[data-cd-upsell-variant-input]');
    var addButton = card.querySelector('[data-rec-add], .cd-upsell__card-add');
    var productCard = card.querySelector('product-card');

    if (priceEl) priceEl.textContent = moneyFmt(price);
    if (compareEl) {
      if (compareAt > price) {
        compareEl.textContent = moneyFmt(compareAt);
        compareEl.classList.remove('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hidden');
      }
    }
    if (titleEl && title) titleEl.textContent = title;
    if (imageEl && image && imageEl.tagName === 'IMG') {
      imageEl.src = image;
      imageEl.alt = imageAlt;
    }
    if (imageLink && url) imageLink.href = url;
    if (titleEl && url) titleEl.href = url;
    if (hiddenInput) hiddenInput.value = variantId;
    if (addButton && addButton.hasAttribute('data-rec-add')) {
      addButton.setAttribute('data-rec-vid', variantId);
      addButton.disabled = button.hasAttribute('disabled');
    }
    if (productCard) productCard.setAttribute('product-id', variantId);
  }

  function buildSlide(product) {
    var variant = product.variants && product.variants[0];
    if (!variant) return '';

    var imgHTML = product.featured_image
      ? '<img src="' + esc(product.featured_image) + '" alt="' + esc(product.title) + '" class="cd-upsell__card-image" loading="lazy" width="99" height="99">'
      : '<span class="cd-upsell__card-image cd-upsell__card-image--empty"></span>';

    var rating = buildStars(product.id);

    // Plain button — no <product-card> wrapper so third-party apps (Lens Advisor, etc.)
    // that scan for <product-card> elements never inject their overlays here.
    // Add-to-cart is handled by the delegated 'data-rec-add' click handler below.
    var addBtn = variant.available
      ? '<button type="button" class="cd-upsell__card-add" data-rec-add data-rec-vid="' + variant.id + '" aria-label="Anadir al carrito">' +
        '<svg class="cd-upsell__plus" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="rgb(0,0,0)" stroke-width="1.6" stroke-linecap="round"/></svg>' +
        '</button>'
      : '<button type="button" class="cd-upsell__card-add" disabled aria-label="Agotado">' +
        '<svg class="cd-upsell__plus" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="rgb(0,0,0)" stroke-width="1.6" stroke-linecap="round"/></svg>' +
        '</button>';

    var variantButtons = buildVariantButtons(product, variant.id);
    var comparePrice = variant.compare_at_price && variant.compare_at_price > variant.price
      ? '<p class="cd-upsell__card-compare" data-cd-upsell-compare-price>' + moneyFmt(variant.compare_at_price) + '</p>'
      : '<p class="cd-upsell__card-compare hidden" data-cd-upsell-compare-price></p>';

    return '<div class="cd-upsell__slide" role="listitem">' +
      '<div class="cd-upsell__card">' +
      '<a href="' + esc(product.url) + '" class="cd-upsell__card-image-link" tabindex="-1" aria-hidden="true">' + imgHTML + '</a>' +
      '<div class="cd-upsell__card-content">' +
      '<div class="cd-upsell__card-head">' +
      '<a href="' + esc(product.url) + '" class="cd-upsell__card-title" data-cd-upsell-title>' + esc((product.title || '').substring(0, 45)) + '</a>' +
      '<div class="cd-upsell__card-rating">' + rating.starsHTML + '</div>' +
      '</div>' +
      variantButtons +
      '<div class="cd-upsell__price-row"><p class="cd-upsell__card-price" data-cd-upsell-price>' + moneyFmt(variant.price) + '</p>' + comparePrice + '</div>' +
      '</div>' +
      addBtn +
      '</div></div>';
  }

  /* ── Recommendation add-to-cart ──────────────────────────────────────────
   * Handles clicks on [data-rec-add] buttons (plain buttons, no <product-card>
   * wrapper so third-party apps never inject overlays).
   * Replicates ProductCard.handleAddToCartClick(): POST /cart/add.js with section
   * rendering, then dispatch 'action:cart:updateCart' on document so DrawerCart
   * re-renders and opens — exactly what the theme's own add-to-cart does.
   * ──────────────────────────────────────────────────────────────────────── */
  var REC_SECTIONS = 'cart-drawer,cart-count-bubble,cart-popup,manual-discount-codes';

  document.addEventListener('click', function (e) {
    var variantButton = e.target.closest && e.target.closest('[data-cd-variant-id]');
    if (variantButton) {
      e.preventDefault();
      e.stopPropagation();
      applySelectedVariant(variantButton.closest('.cd-upsell__card'), variantButton);
      return;
    }

    var btn = e.target.closest && e.target.closest('[data-rec-add]');
    if (!btn) return;

    var variantId = btn.getAttribute('data-rec-vid');
    if (!variantId || btn.disabled) return;

    btn.disabled = true;

    var fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', '1');
    fd.append('sections', REC_SECTIONS);
    fd.append('sections_url', (window.Shopify && Shopify.routes.root) || '/');

    fetch(((window.Shopify && Shopify.routes.root) || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: fd,
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.sections) {
          document.dispatchEvent(new CustomEvent('action:cart:updateCart', {
            bubbles: true,
            detail: { sections: data.sections },
          }));
        }
        // Reset _lastCartSig so next refreshRecommendations call re-fetches
        // (newly added product should now be filtered from recommendations).
        _lastCartSig = null;
        btn.disabled = false;
      })
      .catch(function () { btn.disabled = false; });
  });

  function refreshRecommendations() {
    if (_recUpdating) return;

    var outer = document.querySelector('[cart-upsell-outer]');
    if (!outer) return;
    var track = outer.querySelector(SELECTOR);
    if (!track) return;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) return;

        var sig = cart.items.map(function (i) { return i.product_id; }).sort().join(',');
        if (sig === _lastCartSig) return;
        _lastCartSig = sig;

        var baseId = cart.items[0].product_id;
        var inCartIds = cart.items.map(function (i) { return i.product_id; });

        return fetch('/recommendations/products.json?product_id=' + baseId + '&limit=8&intent=complementary')
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var products = (data.products || [])
              .filter(function (p) { return inCartIds.indexOf(p.id) === -1; })
              .slice(0, 4);

            if (!products.length) return;

            var freshOuter = document.querySelector('[cart-upsell-outer]');
            var freshTrack = freshOuter && freshOuter.querySelector(SELECTOR);
            if (!freshTrack) return;

            _recUpdating = true;
            freshTrack.innerHTML = products.map(buildSlide).join('');
            setTimeout(function () { _recUpdating = false; }, 0);

            var root = freshTrack.closest('.cd-upsell');
            var controls = root && root.querySelector('.cd-upsell__controls');
            if (controls) controls.hidden = products.length <= 1;
            freshTrack.__cdArrowsInit = false;
            setupTrack(freshTrack);
          });
      })
      .catch(function () { /* silent fallback to Liquid-rendered list */ });
  }

  /* ── Observation ────────────────────────────────────────────────────────── */

  function observeOuter() {
    document.querySelectorAll('[cart-upsell-outer]').forEach(function (outer) {
      if (outer.__cdArrowsObserved) return;
      outer.__cdArrowsObserved = true;
      new MutationObserver(function () {
        if (_recUpdating) return;
        scanTracks();
        refreshRecommendations();
      }).observe(outer, { childList: true, subtree: true });
    });
  }

  function init() {
    scanTracks();
    observeOuter();
    refreshRecommendations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
